import React, { useState, useRef } from 'react';
import { supabase } from './supabaseClient.js';
import { BOOKING_STATUSES, STATUS_LABELS, normalizeBookingStatus } from './bookingStatus.js';
import CurrentLocationButton from './CurrentLocationButton.js';

const SHEET_ID = '1mF5G3dY3aa8eJLb-TTRNCmm6B3NGtXa5Yf2BaD9F56s';
const WRITEBACK_URL = process.env.REACT_APP_SHEETS_WRITEBACK_URL;

// CRM fields live on crm_jobs
const EMPTY_CRM = {
  company: '',
  contact_name: '',
  email: '',
  phone: '',
  work_type: '',
  site_description: '',
  location: '',
  internal_notes: '',
  client_notes: '',
  last_contact: '',
  last_completed: '',
  invoice_number: '',
  booking_status: 'not booked',
  value: '',
  estimated_hours: '',
  area_acres: '',
  last_completed_year: '',
  oprec_link: '',
  quote_link: '',
  kmz_link: '',
  image_link: '',
  chemicals_summary: '',
};

// Route-operational fields live on route_items
const EMPTY_ROUTE = {
  completed_by: '',
  chem_ru_l: 0,
  chem_esp_ml: 0,
  chem_navius_g: 0,
  chem_telar_g: 0,
  chem_24d_l: 0,
  chem_clearview_g: 0,
  chem_overdrive_g: 0,
  chem_trillion_l: 0,
};

const CHEM_FIELDS = [
  { key: 'chem_ru_l', label: 'RU (L)', tag: 'RU', unit: 'L' },
  { key: 'chem_esp_ml', label: 'ESP (mL)', tag: 'ESP', unit: 'mL' },
  { key: 'chem_navius_g', label: 'Navius (g)', tag: 'NAV', unit: 'g' },
  { key: 'chem_telar_g', label: 'Telar (g)', tag: 'TEL', unit: 'g' },
  { key: 'chem_24d_l', label: '2,4-D (L)', tag: '24D', unit: 'L' },
  { key: 'chem_clearview_g', label: 'Clearview (g)', tag: 'CLR', unit: 'g' },
  { key: 'chem_overdrive_g', label: 'Overdrive (g)', tag: 'OVR', unit: 'g' },
  { key: 'chem_trillion_l', label: 'Trillion (L)', tag: 'TRL', unit: 'L' },
];

// Serialize chem fields to summary string: "RU:5|ESP:200|NAV:15"
const serializeChemicals = (form) => {
  const parts = [];
  for (const { key, tag } of CHEM_FIELDS) {
    const val = parseFloat(form[key]);
    if (val > 0) parts.push(`${tag}:${val}`);
  }
  return parts.length > 0 ? parts.join('|') : null;
};

// Parse summary string back to individual fields
const parseChemicals = (summary) => {
  const result = {};
  CHEM_FIELDS.forEach(({ key }) => { result[key] = 0; });
  if (!summary) return result;
  const tagToKey = Object.fromEntries(CHEM_FIELDS.map(f => [f.tag, f.key]));
  for (const part of summary.split('|')) {
    const [tag, val] = part.split(':');
    const key = tagToKey[tag?.trim()];
    if (key && val) result[key] = parseFloat(val) || 0;
  }
  return result;
};

const JobFormModal = ({ routeId, dayNumber, sortOrder, existingItem, onClose, onSaved }) => {
  const isEditing = !!existingItem;
  // Capture original CRM values for write-back diffing
  const originalCrm = useRef(null);
  // Track whether the loaded booking_status was a recognized value (for the "!" indicator)
  const originalStatusUnknown = useRef(
    !!existingItem && normalizeBookingStatus(existingItem.booking_status).isUnknown
  );
  const [form, setForm] = useState(() => {
    if (existingItem) {
      const f = {};
      const orig = {};
      for (const key of Object.keys(EMPTY_CRM)) {
        f[key] = existingItem[key] ?? EMPTY_CRM[key];
        orig[key] = existingItem[key] ?? null;
      }
      // Coerce booking_status onto a canonical bucket so the dropdown has a matching option.
      f.booking_status = normalizeBookingStatus(existingItem.booking_status).canonical;
      originalCrm.current = orig;
      for (const key of Object.keys(EMPTY_ROUTE)) {
        f[key] = existingItem[key] ?? EMPTY_ROUTE[key];
      }
      // If no chem data on route_items, parse from CRM summary (previous year reference)
      const hasRouteChem = CHEM_FIELDS.some(({ key }) => parseFloat(existingItem[key]) > 0);
      if (!hasRouteChem && existingItem.chemicals_summary) {
        Object.assign(f, parseChemicals(existingItem.chemicals_summary));
      }
      return f;
    }
    return { ...EMPTY_CRM, ...EMPTY_ROUTE };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState({ contact: false, details: false, notes: false, links: false, chemicals: false });
  const toggleSection = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // CRM payload → crm_jobs table
    const crmPayload = {
      company: form.company || null,
      contact_name: form.contact_name || null,
      email: form.email || null,
      phone: form.phone || null,
      work_type: form.work_type || null,
      site_description: form.site_description || null,
      location: form.location || null,
      internal_notes: form.internal_notes || null,
      client_notes: form.client_notes || null,
      last_contact: form.last_contact || null,
      last_completed: form.last_completed || null,
      invoice_number: form.invoice_number || null,
      booking_status: form.booking_status,
      value: form.value ? parseFloat(form.value) : null,
      estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
      area_acres: form.area_acres ? parseFloat(form.area_acres) : null,
      last_completed_year: form.last_completed_year ? parseInt(form.last_completed_year) : null,
      oprec_link: form.oprec_link || null,
      quote_link: form.quote_link || null,
      kmz_link: form.kmz_link || null,
      image_link: form.image_link || null,
      chemicals_summary: serializeChemicals(form),
    };

    // Route-operational payload → route_items table
    const routePayload = {
      completed_by: form.completed_by || null,
      chem_ru_l: parseFloat(form.chem_ru_l) || 0,
      chem_esp_ml: parseFloat(form.chem_esp_ml) || 0,
      chem_navius_g: parseFloat(form.chem_navius_g) || 0,
      chem_telar_g: parseFloat(form.chem_telar_g) || 0,
      chem_24d_l: parseFloat(form.chem_24d_l) || 0,
      chem_clearview_g: parseFloat(form.chem_clearview_g) || 0,
      chem_overdrive_g: parseFloat(form.chem_overdrive_g) || 0,
      chem_trillion_l: parseFloat(form.chem_trillion_l) || 0,
    };

    let saveError;

    if (isEditing) {
      // Update CRM data on the linked crm_job
      const crmJobId = existingItem.crm_job_id || existingItem.crm_job?.id;
      if (crmJobId) {
        const { error: crmErr } = await supabase.from('crm_jobs').update(crmPayload).eq('id', crmJobId);
        if (crmErr) { saveError = crmErr; }
      }
      // Update route-operational data on route_items
      if (!saveError) {
        const { error: routeErr } = await supabase.from('route_items').update(routePayload).eq('id', existingItem.id);
        if (routeErr) { saveError = routeErr; }
      }
    } else {
      // Creating a new job: first create crm_job, then route_item
      const { data: newCrmJob, error: crmErr } = await supabase
        .from('crm_jobs')
        .insert([crmPayload])
        .select('id')
        .single();

      if (crmErr) {
        saveError = crmErr;
      } else {
        const { error: routeErr } = await supabase.from('route_items').insert([{
          ...routePayload,
          crm_job_id: newCrmJob.id,
          row_type: 'JOB',
          route_id: routeId,
          day_number: dayNumber,
          sort_order: sortOrder,
        }]);
        if (routeErr) { saveError = routeErr; }
      }
    }

    if (saveError) {
      console.error('Error saving job:', saveError);
      setError(`Save failed: ${saveError.message}`);
      setSaving(false);
    } else {
      // Write back changed CRM fields to Google Sheets (fire-and-forget)
      const sheetRow = existingItem?.crm_job?.sheet_row ?? existingItem?.sheet_row;
      if (WRITEBACK_URL && sheetRow && originalCrm.current) {
        const changed = {};
        for (const key of Object.keys(EMPTY_CRM)) {
          const oldVal = originalCrm.current[key];
          const newVal = crmPayload[key];
          if (newVal !== oldVal) changed[key] = newVal;
        }
        if (Object.keys(changed).length > 0) {
          fetch(WRITEBACK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sheetId: SHEET_ID, sheetRow, updates: changed }),
          }).catch(err => console.warn('[Sheets writeback] Failed:', err));
        }
      }
      onSaved();
    }
  };

  const inputClass =
    'w-full px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-12 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-2xl w-full p-6 mb-12">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {isEditing ? 'Edit Job' : 'Add Job'} — Day {dayNumber}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>
        {error && (
          <div className="mb-4 px-3 py-2 bg-red-500/20 border border-red-500 rounded text-sm text-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Core fields — always visible */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Company *</label>
              <input type="text" value={form.company} onChange={(e) => set('company', e.target.value)} required className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Location</label>
              <div className="relative flex items-center">
                <span className="absolute left-2 z-10">
                  <CurrentLocationButton onResolved={(addr) => set('location', addr)} />
                </span>
                <input type="text" value={form.location} onChange={(e) => set('location', e.target.value)} className={`${inputClass} pl-9`} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Value ($)</label>
              <input type="number" step="0.01" value={form.value} onChange={(e) => set('value', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Estimated Hours</label>
              <input type="number" step="0.25" value={form.estimated_hours} onChange={(e) => set('estimated_hours', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Work Type</label>
              <input type="text" value={form.work_type} onChange={(e) => set('work_type', e.target.value)} className={inputClass} placeholder="e.g. Bare ground" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1 flex items-center gap-1">
                Booking Status
                {originalStatusUnknown.current && (
                  <span
                    title={`Sheet had an unrecognized status ("${existingItem?.booking_status || ''}") — interpreted as "${STATUS_LABELS[form.booking_status] || form.booking_status}". Saving will replace it with the canonical value.`}
                    className="text-xs px-1 py-0.5 rounded bg-red-500/30 text-red-200 font-bold leading-none"
                  >
                    !
                  </span>
                )}
              </label>
              <select value={form.booking_status} onChange={(e) => set('booking_status', e.target.value)} className={inputClass}>
                {BOOKING_STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Contact — collapsible */}
          <div>
            <button type="button" onClick={() => toggleSection('contact')} className="text-sm text-gray-300 hover:text-white transition-colors">
              {sections.contact ? '\u25BE' : '\u25B8'} Contact Info {form.contact_name ? `(${form.contact_name})` : ''}
            </button>
            {sections.contact && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Contact Name</label>
                  <input type="text" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Phone</label>
                  <input type="text" value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} />
                </div>
              </div>
            )}
          </div>

          {/* Details — collapsible */}
          <div>
            <button type="button" onClick={() => toggleSection('details')} className="text-sm text-gray-300 hover:text-white transition-colors">
              {sections.details ? '\u25BE' : '\u25B8'} Job Details
            </button>
            {sections.details && (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Area (acres)</label>
                    <input type="number" step="0.1" value={form.area_acres} onChange={(e) => set('area_acres', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Completed By</label>
                    <input type="text" value={form.completed_by} onChange={(e) => set('completed_by', e.target.value)} className={inputClass} placeholder="e.g. Steve" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Invoice #</label>
                    <input type="text" value={form.invoice_number} onChange={(e) => set('invoice_number', e.target.value)} className={inputClass} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Last Contact</label>
                    <input type="text" value={form.last_contact} onChange={(e) => set('last_contact', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Last Completed</label>
                    <input type="text" value={form.last_completed} onChange={(e) => set('last_completed', e.target.value)} className={inputClass} placeholder="e.g. Aug 2025" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Site Description</label>
                  <input type="text" value={form.site_description} onChange={(e) => set('site_description', e.target.value)} className={inputClass} />
                </div>
              </div>
            )}
          </div>

          {/* Notes — collapsible */}
          <div>
            <button type="button" onClick={() => toggleSection('notes')} className="text-sm text-gray-300 hover:text-white transition-colors">
              {sections.notes ? '\u25BE' : '\u25B8'} Notes {(form.client_notes || form.internal_notes) ? '*' : ''}
            </button>
            {sections.notes && (
              <div className="space-y-3 mt-2">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Client Notes</label>
                  <textarea value={form.client_notes} onChange={(e) => set('client_notes', e.target.value)} rows={2} className={inputClass} placeholder="e.g. PO# 260304PM-1" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Internal Notes</label>
                  <textarea value={form.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} rows={2} className={inputClass} />
                </div>
              </div>
            )}
          </div>

          {/* Links — collapsible */}
          <div>
            <button type="button" onClick={() => toggleSection('links')} className="text-sm text-gray-300 hover:text-white transition-colors">
              {sections.links ? '\u25BE' : '\u25B8'} Links & History
            </button>
            {sections.links && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Quote/Estimate Link</label>
                  <input type="url" value={form.quote_link} onChange={(e) => set('quote_link', e.target.value)} className={inputClass} placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Previous OPREC Link</label>
                  <input type="url" value={form.oprec_link} onChange={(e) => set('oprec_link', e.target.value)} className={inputClass} placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">KMZ Site Map Link</label>
                  <input type="url" value={form.kmz_link} onChange={(e) => set('kmz_link', e.target.value)} className={inputClass} placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Image Link</label>
                  <input type="url" value={form.image_link} onChange={(e) => set('image_link', e.target.value)} className={inputClass} placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Last Completed Year</label>
                  <input type="number" min="2020" max="2030" value={form.last_completed_year} onChange={(e) => set('last_completed_year', e.target.value)} className={inputClass} placeholder="e.g. 2025" />
                </div>
              </div>
            )}
          </div>

          {/* Chemicals — collapsible */}
          <div>
            <button type="button" onClick={() => toggleSection('chemicals')} className="text-sm text-gray-300 hover:text-white transition-colors">
              {sections.chemicals ? '\u25BE' : '\u25B8'} Chemical Volumes (reference)
            </button>
            {sections.chemicals && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                {CHEM_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-300 mb-1">{label}</label>
                    <input type="number" step="0.01" value={form[key]} onChange={(e) => set(key, e.target.value)} className={inputClass} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end pt-2 border-t border-gray-500/30">
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-green-700 text-white rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Job'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobFormModal;
